import React from 'react';
import { useDmaBoundaries } from './shared/useDmaBoundaries';
import { featureLabelPoint } from './shared/featureLabelPoint';
import { dmaKeyOf } from './shared/mapFitViewState';
import NationalChoropleth, { type ChoroplethLabel } from './NationalChoropleth';

// CTV-445: national DMA choropleth — the DMA analogue of StateChoropleth. Loads
// WGS84 DMA boundaries (census_dmas, via the dma-boundaries endpoint), keys
// features by 3-digit dma_code — the same codes our geo endpoint returns — and
// renders through the shared NationalChoropleth, so the DMA and State maps use
// the exact same projection/framing. We label only the *delivered* markets
// inside their borders (labeling all 210 would be unreadable); empty markets
// rely on the hover tooltip.

interface DmaDatum {
  dma: string; // 3-digit Nielsen code
  impressions: number;
  completes: number;
}

const dmaNameOf = (f: GeoJSON.Feature): string => String((f.properties as Record<string, unknown>)?.dma_name ?? '');

const DmaChoropleth: React.FC<{
  byDma: DmaDatum[];
  accentColor?: string;
  highlightKey?: string | null;
  highlightColor?: string;
}> = ({ byDma, accentColor, highlightKey, highlightColor }) => {
  const { data: geojson, status, retry } = useDmaBoundaries();

  // Visible retrying/error states for the boundary fetch (CTV-689); the plain
  // 'loading' case keeps NationalChoropleth's default spinner.
  const loadingOverlay = React.useMemo<React.ReactNode>(() => {
    if (status !== 'retrying' && status !== 'error') return undefined;
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50 text-[12px] text-slate-500">
        {status === 'retrying' ? (
          <span>Map data failed to load. Retrying…</span>
        ) : (
          <>
            <span>Couldn&apos;t load metro boundaries.</span>
            <button
              type="button"
              onClick={retry}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-100"
            >
              Try again
            </button>
          </>
        )}
      </div>
    );
  }, [status, retry]);

  const valueByKey = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const d of byDma) m.set(d.dma, d.impressions);
    return m;
  }, [byDma]);

  // Frame the initial view on the delivered markets rather than the whole US.
  const focusKeys = React.useMemo(() => byDma.map((d) => d.dma), [byDma]);

  // Name labels for the delivered markets, anchored inside each border.
  const labels = React.useMemo<ChoroplethLabel[]>(() => {
    if (!geojson) return [];
    const wanted = new Set(byDma.map((d) => d.dma));
    const out: ChoroplethLabel[] = [];
    for (const f of geojson.features) {
      const code = dmaKeyOf(f);
      if (!code || !wanted.has(code) || !f.geometry) continue;
      const pos = featureLabelPoint(f.geometry);
      if (pos) out.push({ position: pos, text: dmaNameOf(f), key: code });
    }
    return out;
  }, [geojson, byDma]);

  return (
    <NationalChoropleth
      geojson={geojson}
      valueByKey={valueByKey}
      keyOf={dmaKeyOf}
      nameOf={dmaNameOf}
      labels={labels}
      labelBackground
      idPrefix="dma"
      focusKeys={focusKeys}
      accentColor={accentColor}
      highlightKey={highlightKey}
      highlightColor={highlightColor}
      loadingOverlay={loadingOverlay}
    />
  );
};

export default DmaChoropleth;
